/**
 * AttendeeGroupView — one row in the attendees block. Renders an
 * isOjb pill toggle + editable firm text + ":" separator +
 * editable members text (comma-separated). All text regions are
 * NodeViewContent so cursor / marks / selection work normally.
 *
 * Phase 6: replaces the legacy form-input row. The whole row
 * participates in the editor's contenteditable surface.
 */
import {
  NodeViewContent,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { X } from 'lucide-react';
import { ReorderControls } from './ReorderControls.tsx';

export function AttendeeGroupView({
  node,
  updateAttributes,
  editor,
  getPos,
}: NodeViewProps) {
  const isOjb = node.attrs.isOjb === true;
  const isEditable = editor.isEditable;

  const removeGroup = () => {
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (pos === null) return;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  };

  // Add a left-side accent so OJB groups are visually distinct
  // from external groups WITHOUT a redundant "OJB" pill next to
  // an "OJB" firm name. Toggle moves to a hover-only affordance
  // on the right (alongside the X delete button).
  const accentClass = isOjb
    ? 'border-l-2 border-pine/50 pl-2'
    : 'border-l-2 border-transparent pl-2';

  return (
    <NodeViewWrapper
      className={
        'tt-attendee-group group/row relative flex items-baseline gap-1.5 text-[12px] leading-relaxed text-charcoal ' +
        accentClass
      }
      data-node="attendeeGroup"
    >
      {/* Reorder gutter — up arrow + drag handle + down arrow.
          Floats in the page's left margin, inherits hover state
          via group/row on the wrapper. */}
      <ReorderControls
        editor={editor}
        getPos={getPos as () => number | undefined}
        label="attendee group"
      />

      {/* Editable firm + members on the same baseline. We can't use
          NodeViewContent twice (it must appear exactly once), so we
          render a single content slot and rely on the schema
          (firmText membersText) — the firm renders first, then the
          colon separator, then the members. CSS arranges them on a
          single flowing line. */}
      <NodeViewContent
        as="span"
        className="tt-attendee-group__content flex-1"
      />

      {isEditable && (
        <div
          contentEditable={false}
          className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover/row:opacity-100"
        >
          <button
            type="button"
            onClick={() => updateAttributes({ isOjb: !isOjb })}
            title={isOjb ? 'Mark as external firm' : 'Mark as OJB'}
            className={
              'select-none rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] transition-colors ' +
              (isOjb
                ? 'bg-pine/15 text-pine hover:bg-pine/25'
                : 'bg-charcoal/10 text-charcoal hover:bg-charcoal/15')
            }
          >
            {isOjb ? 'OJB' : 'External'}
          </button>
          <button
            type="button"
            onClick={removeGroup}
            title="Remove group"
            aria-label="Remove attendee group"
            className="select-none text-charcoal hover:text-pine"
          >
            <X size={11} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}
